import SettingsIcon from '@mui/icons-material/Settings';
import { Alert, Container, Grid, Link, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { find, isMatch } from 'lodash';
import { ReactNode, useState } from 'react';

import { UserDisplayPreferences, usePostDisplayPreferences, useUserAccount } from '@/api/userAccount';

/**
 * Accounts created before this date have the option of classic format vs. 2025-02.
 * Accounts created after this date have defaulted to 2025-02 and don't get a choice,
 */
const CLASSIC_CSV_LEGACY_END_DATE = Date.parse('2025-02-01T00:00:00Z');

type DisplayPreferenceItem = {
    label: string;
    name: keyof UserDisplayPreferences;
    toggleButtons: {
        label: string;
        value: string;
    }[];
    defaultValue?: string;
    infoNote?: string | ReactNode;
    hide?: boolean;
};

const METRIC_VALUES: Partial<UserDisplayPreferences> = {
    distance: 'm',
    measuringDistance: 'km',
    speed: 'm/s',
    temperature: 'C',
    pressure: 'm',
} as const;

const IMPERIAL_VALUES: Partial<UserDisplayPreferences> = {
    distance: 'ft',
    measuringDistance: 'mi',
    speed: 'mph',
    temperature: 'F',
    pressure: 'bar',
} as const;

const displayPreferencesItems: DisplayPreferenceItem[] = [
    {
        label: 'Length',
        name: 'distance',
        toggleButtons: [
            { label: 'Meters', value: 'm' },
            { label: 'Feet', value: 'ft' },
        ],
    },
    {
        label: 'Temperature',
        name: 'temperature',
        toggleButtons: [
            { label: 'Celsius', value: 'C' },
            { label: 'Fahrenheit', value: 'F' },
        ],
    },
    {
        label: 'Time zone',
        name: 'timeZone',
        toggleButtons: [
            { label: 'Local', value: 'local' },
            { label: 'UTC', value: 'UTC' },
        ],
    },
    {
        label: 'Water pressure',
        name: 'pressure',
        toggleButtons: [
            { label: 'Meters depth', value: 'm' },
            { label: 'Bar', value: 'bar' },
        ],
    },
    {
        label: 'Distance',
        name: 'measuringDistance',
        toggleButtons: [
            { label: 'Kilometers', value: 'km' },
            { label: 'Miles', value: 'mi' },
        ],
    },
    {
        label: 'Speed',
        name: 'speed',
        toggleButtons: [
            { label: 'Meters per second', value: 'm/s' },
            { label: 'Miles per hour', value: 'mph' },
            { label: 'Knots', value: 'knots' },
        ],
    },
    {
        label: 'Historical data CSV format',
        name: 'csvFormat',
        toggleButtons: [
            { label: 'Classic', value: 'classic' },
            { label: 'New (Recommended)', value: '2025-02' },
        ],
        // We'll un-hide it for old accounts
        hide: true,
        defaultValue: 'classic',
        infoNote: (
            <>
                Switch to the new format to enable wider time ranges. Wave data spectra will be combined into a single
                CSV column. See{' '}
                <Link
                    href="https://sofarocean.notion.site/Spotter-dashboard-file-download-CSV-guide-04b9edce653644d3a78f844fe3ab7f1e"
                    variant="body2"
                >
                    CSV Guide
                </Link>{' '}
                for more details.
            </>
        ),
    },
];

export const DisplayPreferences = () => {
    const [isRotating, setIsRotating] = useState('false');
    const { data: userAccountResponse, isFetching: isDisplayPreferencesFetching } = useUserAccount();

    const { displayPreferences, createdAt: accountCreatedAt } = userAccountResponse?.data?.user ?? {};
    const {
        mutateAsync: postDisplayPreferences,
        isPending: isPostDisplayPreferencesPending,
        isError: isErrorPostingDisplayPreferences,
        error: postDisplayPreferencesError,
    } = usePostDisplayPreferences();

    if (accountCreatedAt && Date.parse(accountCreatedAt) < CLASSIC_CSV_LEGACY_END_DATE) {
        const csvPreferenceItem = find(displayPreferencesItems, { name: 'csvFormat' });
        if (csvPreferenceItem) {
            csvPreferenceItem.hide = false;
        }
    }

    let currentSystem: 'metric' | 'imperial' | null = null;
    if (displayPreferences) {
        if (isMatch(displayPreferences, METRIC_VALUES)) {
            currentSystem = 'metric';
        } else if (isMatch(displayPreferences, IMPERIAL_VALUES)) {
            currentSystem = 'imperial';
        }
    }

    const onSubmit = async (formData: UserDisplayPreferences) => {
        setIsRotating('true');
        try {
            await postDisplayPreferences(formData);
        } catch (err) {
            console.error(err);
        }
    };

    const handleButtonChange =
        (name: keyof UserDisplayPreferences) => (event: React.MouseEvent<HTMLElement>, value: string) => {
            if (value === null) {
                // disallow unselecting. One button must always be selected.
                return;
            }
            if (!displayPreferences) {
                return;
            }
            onSubmit({ ...displayPreferences, [name]: value });
        };

    const onSetUnitSystem = (value: string) => {
        if (!displayPreferences) {
            return;
        }
        if (value === 'metric') {
            onSubmit({ ...displayPreferences, ...METRIC_VALUES });
        } else if (value === 'imperial') {
            onSubmit({ ...displayPreferences, ...IMPERIAL_VALUES });
        }
    };

    return (
        <>
            <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center' }}>
                <SettingsIcon
                    fontSize="medium"
                    data-isrotating={isRotating}
                    onAnimationEnd={() => setIsRotating('false')}
                    sx={{
                        mr: 1,
                    }}
                />
                Display Preferences
            </Typography>
            <Container
                data-testid="avatar-section-preferences"
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    my: 2,
                }}
            >
                <ToggleButtonGroup
                    onChange={(_e, value) => onSetUnitSystem(value)}
                    exclusive
                    aria-label="Unit system"
                    size="small"
                    value={currentSystem}
                    disabled={isDisplayPreferencesFetching || isPostDisplayPreferencesPending}
                    sx={{ justifyContent: 'center' }}
                >
                    <ToggleButton value="metric">Set to metric</ToggleButton>
                    <ToggleButton value="imperial">Set to imperial</ToggleButton>
                </ToggleButtonGroup>
                {isErrorPostingDisplayPreferences && (
                    <Alert severity="error">
                        Error updating display preferences. The server responded with:{' '}
                        {postDisplayPreferencesError.message}
                    </Alert>
                )}
                {displayPreferencesItems.map(
                    (optionItem) =>
                        !optionItem.hide && (
                            <Grid
                                key={optionItem.label}
                                container
                                spacing={1}
                                sx={{ justifyContent: 'space-between', alignItems: 'center' }}
                            >
                                <Grid>
                                    <Typography variant="h5">{optionItem.label}</Typography>
                                </Grid>
                                {optionItem.infoNote && (
                                    <Grid>
                                        <Typography variant="body2">{optionItem.infoNote}</Typography>
                                    </Grid>
                                )}
                                <Grid>
                                    <ToggleButtonGroup
                                        onChange={handleButtonChange(optionItem.name)}
                                        exclusive
                                        aria-label={optionItem.name}
                                        size="small"
                                        value={displayPreferences?.[optionItem.name] ?? optionItem.defaultValue}
                                        disabled={isDisplayPreferencesFetching || isPostDisplayPreferencesPending}
                                    >
                                        {optionItem.toggleButtons.map((btn) => (
                                            <ToggleButton
                                                key={`${btn.value}-${btn.label}`}
                                                value={btn.value}
                                                aria-label={btn.label}
                                            >
                                                {btn.label}
                                            </ToggleButton>
                                        ))}
                                    </ToggleButtonGroup>
                                </Grid>
                            </Grid>
                        )
                )}
            </Container>
            <style>{`
                [data-isrotating='true'] {
                    animation-name: spin;
                    animation-duration: 3s;
                    animation-iteration-count: 1;
                }
                @keyframes spin {
                    0% {
                        transform: rotate(0deg);
                    }
                    100% {
                        transform: rotate(540deg);
                    }
                }
            `}</style>
        </>
    );
};
